/* The product list — designs/intake-unified-form.md UF21, reshaped by
   designs/teams-and-products.md (Oct 1 2026, per Lewis).

   TP2 (option B, Lewis Oct 2 2026): a team OWNS ONE OR MORE products —
   platform teams included (PDP 2.5 runs platform products like PRAK) — — each a PDP track with its own name, MARKET (a grouping
   across teams, e.g. Construction), VEHICLES (many, added often) and PDP
   record (product-pdp.ts, keyed by the product id). A team's first product
   starts with the team's name; a new vehicle program is a new product.

   A product no team claims yet (`teamId: null`) is shown as needing review
   until an Admin files it under a team. Same idiom as canonical-teams.ts:
   append-only revisions, a stable slug that never re-slugs, retire is a
   flag. */

import type { Stamp, UserRef } from "./organizer-roles";
import { ENTERED_SRC } from "./projects";
import { normalizeName, slugId } from "./roles";
import { CONSOLIDATION_DATE, CONSOLIDATION_SRC } from "./canonical-teams";

export interface Product {
  id: string;
  /* the product's own name (its team's, by default, for a team's first) */
  name: string;
  /* the product or other team this product belongs to; null or absent =
     not filed under a team yet (needs review) */
  teamId?: string | null;
  /* a grouping across teams (Construction, Ag, …); null or absent = not
     recorded */
  market?: string | null;
  /* out of circulation, never deleted — absent means in circulation */
  retired?: boolean;
  recorded: Stamp;
}

/* an Admin's correction (TP3): file it under a team, set its market,
   retire it. `name` only moves on a product with no team — a team's
   product is renamed on Teams. */
export interface ProductEdit {
  name?: string;
  teamId?: string | null;
  market?: string | null;
  retired?: boolean;
}

/* an Admin's new product (TP2): a name, and the team it belongs to and its
   market when known */
export interface ProductInput {
  name: string;
  teamId?: string | null;
  market?: string | null;
}

/* A vehicle of one product (TP4). Its name is unique WITHIN its product
   only: two teams may use the same vehicle name for different purposes. */
export interface Vehicle {
  /* `${productId}.${slug}` — stable, never re-slugs */
  id: string;
  productId: string;
  name: string;
  /* added from the intake form, not yet filed by an Admin (TP5) */
  needsReview?: boolean;
  retired?: boolean;
  recorded: Stamp;
}

export interface VehicleInput {
  productId: string;
  name: string;
}

export interface VehicleEdit {
  name?: string;
  retired?: boolean;
  needsReview?: boolean;
}

const NAME_MAX = 200;
const MARKET_MAX = 100;

const nameReason = (name: unknown): string | null => {
  if (typeof name !== "string" || !normalizeName(name)) return "name is required";
  if (name.length > NAME_MAX) return `name is over ${NAME_MAX} characters`;
  return null;
};

const marketReason = (market: unknown): string | null => {
  if (market === null || market === undefined) return null;
  if (typeof market !== "string" || !market.trim()) return "market must be text, or null for not recorded";
  if (market.length > MARKET_MAX) return `market is over ${MARKET_MAX} characters`;
  return null;
};

/* one name, one identity: a clash names the standing product so the caller
   picks it instead of forking the vocabulary */
const clashReason = (name: string, existing: Product[], exceptId?: string): string | null => {
  const id = slugId(name);
  const wanted = normalizeName(name);
  const clash = existing.find(
    (p) => p.id !== exceptId && (p.id === id || normalizeName(p.name) === wanted),
  );
  return clash ? `already exists as "${clash.name}" (${clash.id}) — pick it instead` : null;
};

/* `teamIds` = the teams a product may belong to (any live team with a type) */
export const validateProductInput = (
  input: ProductInput,
  existing: Product[],
  teamIds?: readonly string[],
): string | null =>
  nameReason(input?.name) ??
  clashReason(input.name, existing) ??
  marketReason(input.market) ??
  (input.teamId != null && teamIds !== undefined && !teamIds.includes(input.teamId)
    ? `"${input.teamId}" is not a current team with a type`
    : null);

/* `teamIds` = the teams a product may be filed under (product or other) */
export function validateProductEdit(
  edit: ProductEdit,
  existing: Product[],
  id: string,
  teamIds: readonly string[] = [],
): string | null {
  if (
    edit.name === undefined &&
    edit.retired === undefined &&
    edit.market === undefined &&
    edit.teamId === undefined
  )
    return "nothing to change";
  if (edit.name !== undefined) {
    const bad = nameReason(edit.name) ?? clashReason(edit.name, existing, id);
    if (bad) return bad;
  }
  if (edit.retired !== undefined && typeof edit.retired !== "boolean") return "retired must be a boolean";
  const market = marketReason(edit.market);
  if (market) return market;
  /* a team may own several products (TP2, option B); filing or moving one
     only needs a live team with a type to land on */
  if (edit.teamId !== undefined && edit.teamId !== null && !teamIds.includes(edit.teamId))
    return `"${edit.teamId}" is not a current team with a type`;
  return null;
}

/* the correction is a new revision; the id never re-slugs */
export const applyProductEdit = (current: Product, edit: ProductEdit, by: UserRef, date: string): Product => ({
  ...current,
  ...(edit.name !== undefined ? { name: edit.name.trim() } : {}),
  ...(edit.teamId !== undefined ? { teamId: edit.teamId } : {}),
  ...(edit.market !== undefined ? { market: edit.market === null ? null : edit.market.trim() } : {}),
  ...(edit.retired !== undefined ? { retired: edit.retired } : {}),
  id: current.id,
  recorded: { source: ENTERED_SRC, date, by },
});

export const vehicleId = (productId: string, name: string): string => `${productId}.${slugId(name)}`;

export function validateVehicleInput(
  input: VehicleInput,
  products: Product[],
  vehicles: Vehicle[],
): string | null {
  const product = products.find((p) => p.id === input?.productId);
  if (!product) return `"${input?.productId}" is not a known product`;
  if (product.retired) return `${product.name} is retired`;
  const bad = nameReason(input.name);
  if (bad) return bad;
  const clash = vehicles.find(
    (v) =>
      v.productId === product.id &&
      (v.id === vehicleId(product.id, input.name) || normalizeName(v.name) === normalizeName(input.name)),
  );
  return clash ? `${product.name} already has "${clash.name}" — pick it instead` : null;
}

export function validateVehicleEdit(edit: VehicleEdit, vehicles: Vehicle[], id: string): string | null {
  if (edit.name === undefined && edit.retired === undefined && edit.needsReview === undefined)
    return "nothing to change";
  const current = vehicles.find((v) => v.id === id);
  if (edit.name !== undefined) {
    const bad = nameReason(edit.name);
    if (bad) return bad;
    if (
      current &&
      vehicles.some(
        (v) => v.id !== id && v.productId === current.productId && normalizeName(v.name) === normalizeName(edit.name!),
      )
    )
      return "that product already has a vehicle by that name";
  }
  if (edit.retired !== undefined && typeof edit.retired !== "boolean") return "retired must be a boolean";
  if (edit.needsReview !== undefined && typeof edit.needsReview !== "boolean")
    return "needsReview must be a boolean";
  return null;
}

export const applyVehicleEdit = (current: Vehicle, edit: VehicleEdit, by: UserRef, date: string): Vehicle => ({
  ...current,
  ...(edit.name !== undefined ? { name: edit.name.trim() } : {}),
  ...(edit.retired !== undefined ? { retired: edit.retired } : {}),
  ...(edit.needsReview !== undefined ? { needsReview: edit.needsReview } : {}),
  id: current.id,
  recorded: { source: ENTERED_SRC, date, by },
});

/* ── The Oct 1 2026 consolidation's products (TP7, per Lewis): one product
   per product or other team, its market, its vehicles. Product ids are the
   team ids. Migration 022 appends these; the memory store plants them. ── */
export const CONSOLIDATED_PRODUCTS: readonly { teamId: string; market: string; vehicles: readonly string[] }[] = [
  { teamId: "logistics", market: "Logistics", vehicles: ["Yard Truck"] },
  { teamId: "reach-stacker", market: "Material Handling", vehicles: ["Reach Stacker"] },
  { teamId: "telehandler", market: "Material Handling", vehicles: ["Telehandler"] },
  { teamId: "ctl", market: "Material Handling", vehicles: ["CTL"] },
  { teamId: "ag-tractor", market: "Ag", vehicles: ["8R", "9R"] },
  { teamId: "dozer", market: "Construction", vehicles: ["Dozer"] },
  { teamId: "compactor", market: "Construction", vehicles: ["Compactor"] },
  { teamId: "wheel-loader", market: "Construction", vehicles: ["Wheel Loader"] },
  { teamId: "adt-water-truck", market: "Construction", vehicles: ["ADT", "Water Truck"] },
  { teamId: "blitz", market: "Construction", vehicles: ["Excavator (CAT 352)", "Komatsu HM460"] },
  { teamId: "fairway-mower", market: "Landscaping", vehicles: ["Fairway Mower"] },
  { teamId: "commercial-landscaping", market: "Landscaping", vehicles: ["M.52 mower"] },
];

/* which vehicle an effort filed under an old row gets (TP7). Absent = the
   row had several vehicles, so the vehicle stays NOT RECORDED rather than
   guessed (Ag Tractor: 8R or 9R; ADT / Water Truck: which one). */
export const CONSOLIDATED_EFFORT_VEHICLE: Readonly<Record<string, { productId: string; vehicle: string }>> = {
  logistics: { productId: "logistics", vehicle: "Yard Truck" },
  "reach-stacker": { productId: "reach-stacker", vehicle: "Reach Stacker" },
  telehandler: { productId: "telehandler", vehicle: "Telehandler" },
  ctl: { productId: "ctl", vehicle: "CTL" },
  dozer: { productId: "dozer", vehicle: "Dozer" },
  compactor: { productId: "compactor", vehicle: "Compactor" },
  "wheel-loader": { productId: "wheel-loader", vehicle: "Wheel Loader" },
  "fairway-mower": { productId: "fairway-mower", vehicle: "Fairway Mower" },
  "commercial-landscaping": { productId: "commercial-landscaping", vehicle: "M.52 mower" },
  "blitz-excavator": { productId: "blitz", vehicle: "Excavator (CAT 352)" },
  "blitz-hm460": { productId: "blitz", vehicle: "Komatsu HM460" },
};
/* rows whose efforts get the product but no vehicle */
export const CONSOLIDATED_EFFORT_PRODUCT_ONLY: readonly string[] = ["ag-tractor", "adt-water-truck"];

export const seedProducts = (teamName: (id: string) => string): Product[] =>
  CONSOLIDATED_PRODUCTS.map((p) => ({
    id: p.teamId,
    name: teamName(p.teamId),
    teamId: p.teamId,
    market: p.market,
    recorded: { source: CONSOLIDATION_SRC, date: CONSOLIDATION_DATE },
  }));

export const seedVehicles = (): Vehicle[] =>
  CONSOLIDATED_PRODUCTS.flatMap((p) =>
    p.vehicles.map((name) => ({
      id: vehicleId(p.teamId, name),
      productId: p.teamId,
      name,
      recorded: { source: CONSOLIDATION_SRC, date: CONSOLIDATION_DATE },
    })),
  );

export const productSlug = slugId;
